import { create } from 'zustand';

/**
 * Entity type for navigation/filtering
 */
export type EntityTypeFilter =
  | 'all'
  | 'tasks'
  | 'events'
  | 'calls'
  | 'videocalls'
  | 'emails'
  | 'messages'
  | 'files'
  | 'contacts'
  | 'invoices'
  | 'projects';

/**
 * View mode for main content
 */
export type ViewMode =
  | 'dashboard'
  | 'tasks'
  | 'calendar'
  | 'calls'
  | 'messages'
  | 'emails'
  | 'files'
  | 'contacts'
  | 'accounting'
  | 'project';

/**
 * Selected entity info
 */
export interface SelectedEntity {
  id: string;
  type: string;
}

/**
 * App store state
 */
interface AppState {
  // Sidebar
  sidebarOpen: boolean;
  sidebarCollapsed: boolean;

  // Filters
  activeFilter: EntityTypeFilter;
  searchQuery: string;

  // Navigation
  currentView: ViewMode;
  selectedEntity: SelectedEntity | null;
  highlightedEntityId: string | null;

  // UI State
  showAIPanel: boolean;
  showConnectionsPanel: boolean;

  // Timer
  activeTimerTaskId: string | null;
  timerStartTime: number | null;

  // Actions
  setSidebarOpen: (open: boolean) => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
  setActiveFilter: (filter: EntityTypeFilter) => void;
  setSearchQuery: (query: string) => void;
  setCurrentView: (view: ViewMode) => void;
  selectEntity: (entity: SelectedEntity | null) => void;
  highlightEntity: (id: string | null) => void;
  toggleAIPanel: () => void;
  toggleConnectionsPanel: () => void;
  startTimer: (taskId: string) => void;
  stopTimer: () => void;

  // Navigation helpers
  navigateToEntity: (id: string, type: string) => void;
}

/**
 * Get view mode from entity type
 */
function getViewFromEntityType(type: string): ViewMode {
  const viewMap: Record<string, ViewMode> = {
    tasks: 'tasks',
    events: 'calendar',
    calls: 'calls',
    videocalls: 'calls',
    emails: 'emails',
    sms: 'messages',
    telegram: 'messages',
    whatsapp: 'messages',
    files: 'files',
    image: 'files',
    audio: 'files',
    contacts: 'contacts',
    profiles: 'contacts',
    invoices: 'accounting',
    projects: 'project',
    companies: 'contacts',
  };
  return viewMap[type] || 'dashboard';
}

/**
 * Main app store
 */
export const useAppStore = create<AppState>((set, get) => ({
  // Initial state
  sidebarOpen: true,
  sidebarCollapsed: false,
  activeFilter: 'all',
  searchQuery: '',
  currentView: 'dashboard',
  selectedEntity: null,
  highlightedEntityId: null,
  showAIPanel: false,
  showConnectionsPanel: false,
  activeTimerTaskId: null,
  timerStartTime: null,

  // Actions
  setSidebarOpen: (open) => set({ sidebarOpen: open }),
  setSidebarCollapsed: (collapsed) => set({ sidebarCollapsed: collapsed }),
  setActiveFilter: (filter) => set({ activeFilter: filter }),
  setSearchQuery: (query) => set({ searchQuery: query }),
  setCurrentView: (view) => set({ currentView: view }),

  selectEntity: (entity) => set({
    selectedEntity: entity,
    highlightedEntityId: entity?.id || null
  }),

  highlightEntity: (id) => set({ highlightedEntityId: id }),

  toggleAIPanel: () => set((state) => ({ showAIPanel: !state.showAIPanel })),
  toggleConnectionsPanel: () => set((state) => ({
    showConnectionsPanel: !state.showConnectionsPanel
  })),

  startTimer: (taskId) => set({
    activeTimerTaskId: taskId,
    timerStartTime: Date.now()
  }),

  stopTimer: () => set({
    activeTimerTaskId: null,
    timerStartTime: null
  }),

  // Navigate to an entity - changes view and highlights
  navigateToEntity: (id, type) => {
    const view = getViewFromEntityType(type);
    set({
      currentView: view,
      selectedEntity: { id, type },
      highlightedEntityId: id,
    });

    // Clear highlight after animation
    setTimeout(() => {
      set({ highlightedEntityId: null });
    }, 2000);
  },
}));
